import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Plus, Trash2, Truck } from 'lucide-react';
import { SellerShippingPromotions } from '@/components/shipping-promos/SellerShippingPromotions';
import { SellerShippingRefunds } from '@/components/shipping-promos/SellerShippingRefunds';
import { SellerLogisticsMode } from '@/components/logistics/SellerLogisticsMode';

const emptyZone = { rate: '', days_min: '1', days_max: '3', free: false };

const ZoneRow = ({ zone, departments, withCity, onChange, onRemove, testid }) => (
  <div className="grid grid-cols-2 md:grid-cols-[1.4fr,1.2fr,1fr,0.7fr,0.7fr,auto,auto] gap-2 items-center" data-testid={testid}>
    <select value={zone.department} onChange={(e) => onChange({ ...zone, department: e.target.value })}
      className="h-9 border border-slate-300 px-2 text-sm bg-white" data-testid={`${testid}-department`}>
      <option value="">Departamento…</option>
      {departments.map((d) => <option key={d} value={d}>{d}</option>)}
    </select>
    {withCity ? (
      <Input value={zone.city} onChange={(e) => onChange({ ...zone, city: e.target.value })} placeholder="Ciudad"
        className="rounded-none h-9" data-testid={`${testid}-city`} />
    ) : <div className="hidden md:block text-xs text-slate-400">Todo el departamento</div>}
    <Input type="number" min="0" value={zone.rate} onChange={(e) => onChange({ ...zone, rate: e.target.value })}
      placeholder="Tarifa COP" className="rounded-none h-9" data-testid={`${testid}-rate`} />
    <Input type="number" min="0" value={zone.days_min} onChange={(e) => onChange({ ...zone, days_min: e.target.value })}
      placeholder="Días mín." className="rounded-none h-9" data-testid={`${testid}-days-min`} />
    <Input type="number" min="0" value={zone.days_max} onChange={(e) => onChange({ ...zone, days_max: e.target.value })}
      placeholder="Días máx." className="rounded-none h-9" data-testid={`${testid}-days-max`} />
    <label className="text-xs flex items-center gap-1 whitespace-nowrap">
      <input type="checkbox" checked={zone.free} onChange={(e) => onChange({ ...zone, free: e.target.checked })}
        data-testid={`${testid}-free`} /> Gratis
    </label>
    <button onClick={onRemove} className="text-rose-700" data-testid={`${testid}-remove`}><Trash2 className="h-4 w-4" /></button>
  </div>
);

const toApiZone = (z, withCity) => ({
  department: z.department, ...(withCity ? { city: z.city } : {}),
  rate: parseFloat(z.rate || 0), days_min: parseInt(z.days_min || 0, 10), days_max: parseInt(z.days_max || 0, 10), free: !!z.free,
});
const fromApiZone = (z) => ({ ...z, rate: String(z.rate), days_min: String(z.days_min), days_max: String(z.days_max) });

export default function SellerShipping() {
  const [departments, setDepartments] = useState([]);
  const [carrier, setCarrier] = useState('');
  const [freeOver, setFreeOver] = useState('');
  const [depts, setDepts] = useState([]);
  const [cities, setCities] = useState([]);
  const [saving, setSaving] = useState(false);

  useSEO({ title: 'Envíos | Panel del vendedor', description: 'Configura transportadora, cobertura y tarifas de envío.', canonical: '/vendedor/envios' });

  useEffect(() => {
    api.get('/geo/departments').then(({ data }) => setDepartments(data.items || []));
    api.get('/sellers/me/shipping').then(({ data }) => {
      const c = data.config;
      if (!c) return;
      setCarrier(c.carrier || '');
      setFreeOver(c.free_over ?? '');
      setDepts((c.departments || []).map(fromApiZone));
      setCities((c.cities || []).map(fromApiZone));
    });
  }, []);

  const save = async () => {
    const all = [...depts, ...cities];
    if (all.some((z) => !z.department || z.rate === '') || cities.some((z) => !(z.city || '').trim())) {
      toast.error('Completa departamento, ciudad y tarifa en todas las filas');
      return;
    }
    setSaving(true);
    try {
      await api.put('/sellers/me/shipping', {
        carrier, free_over: freeOver === '' ? null : parseFloat(freeOver),
        departments: depts.map((z) => toApiZone(z, false)), cities: cities.map((z) => toApiZone(z, true)),
      });
      toast.success('Configuración de envíos guardada');
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (Array.isArray(d) ? d[0]?.msg : 'Revisa los datos');
      toast.error(String(msg || 'Revisa los datos').replace(/^Value error,\s*/, ''));
    } finally { setSaving(false); }
  };

  const upd = (list, setList) => (i, z) => setList(list.map((x, j) => (j === i ? z : x)));

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Vendedor</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1 flex items-center gap-2">
          <Truck className="h-7 w-7" /> Envíos
        </h1>
        <p className="mt-2 text-sm text-slate-600 max-w-3xl">
          Tarifa fija por pedido según el destino. Si la ciudad tiene una excepción, se usa esa tarifa; si no, la del departamento.
          Destinos sin cobertura no pueden comprarte. Envío gratis (no acumulable): 1) zona marcada gratis, 2) todos los productos del pedido marcados con envío gratis, 3) compra desde el monto mínimo.
          Recibes el envío completo en tu desembolso y no paga comisión.
        </p>

        <SellerLogisticsMode departments={departments} />

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 border border-slate-200 bg-white p-5">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Transportadora</label>
            <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Ej: Servientrega"
              className="rounded-none mt-1" data-testid="shipping-carrier-input" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Envío gratis desde (COP, opcional)</label>
            <Input type="number" min="0" value={freeOver} onChange={(e) => setFreeOver(e.target.value)} placeholder="Ej: 300000"
              className="rounded-none mt-1" data-testid="shipping-free-over-input" />
          </div>
        </div>

        <Section title="Cobertura por departamento" onAdd={() => setDepts([...depts, { ...emptyZone, department: '' }])} testid="shipping-add-department">
          {depts.map((z, i) => (
            <ZoneRow key={i} zone={z} departments={departments} onChange={(nz) => upd(depts, setDepts)(i, nz)}
              onRemove={() => setDepts(depts.filter((_, j) => j !== i))} testid={`shipping-dept-${i}`} />
          ))}
        </Section>
        <Section title="Excepciones por ciudad" onAdd={() => setCities([...cities, { ...emptyZone, department: '', city: '' }])} testid="shipping-add-city">
          {cities.map((z, i) => (
            <ZoneRow key={i} zone={z} departments={departments} withCity onChange={(nz) => upd(cities, setCities)(i, nz)}
              onRemove={() => setCities(cities.filter((_, j) => j !== i))} testid={`shipping-city-${i}`} />
          ))}
        </Section>

        <Button onClick={save} disabled={saving || !carrier.trim()} className="mt-6 rounded-none bg-slate-900 hover:bg-slate-800 h-11 font-bold"
          data-testid="shipping-save-btn">
          {saving ? 'Guardando…' : 'Guardar configuración'}
        </Button>
        <SellerShippingPromotions />
        <SellerShippingRefunds />
      </div>
    </Layout>
  );
}

const Section = ({ title, onAdd, testid, children }) => (
  <div className="mt-6 border border-slate-200 bg-white p-5">
    <div className="flex items-center justify-between">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{title}</div>
      <Button variant="outline" size="sm" className="rounded-none" onClick={onAdd} data-testid={testid}>
        <Plus className="h-4 w-4 mr-1" /> Agregar
      </Button>
    </div>
    <div className="mt-3 hidden md:grid grid-cols-[1.4fr,1.2fr,1fr,0.7fr,0.7fr,auto,auto] gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
      <span>Departamento</span><span>Ciudad</span><span>Tarifa por pedido</span><span>Días mín.</span><span>Días máx.</span><span>Gratis</span><span />
    </div>
    <div className="mt-2 space-y-2">{children}</div>
  </div>
);
